import { differenceInDays, format } from 'date-fns';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoalContextMenu } from 'shared/components/connected/goal-context-menu';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { Paths } from 'shared/routes';
import { DateRange } from 'shared/types/date-range';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { Timestamp } from 'shared/types/timestamp';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import * as Styled from './goal-bar.style';
import { MovementModes, useDragHandlers } from './use-drag-handlers';

export type GoalBarProps = {
  goal: Goal;
  goals: Goal[];
  lifeAreas: LifeArea[];
  dateRange: DateRange;
  dayWidth: number;
  onUpdateDates: (
    goalId: string,
    newStartDate: Timestamp,
    newEndDate: Timestamp,
  ) => void;
};

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

export const GoalBar: React.FC<GoalBarProps> = ({
  goal,
  goals,
  lifeAreas,
  dateRange,
  dayWidth,
  onUpdateDates,
}) => {
  const itemContainerRef = useRef<HTMLDivElement>(null);
  const resizeStartHandle = useRef<HTMLDivElement>(null);
  const resizeEndHandle = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState({
    disabled: !!goal.frozenAt,
  });

  const wasDragged = useRef(false);

  const onNavigate = useCallback(() => {
    if (wasDragged.current) {
      wasDragged.current = false;
      return;
    }
    navigate(replaceUrlParams(Paths.GoalDetail, { id: goal.id }));
  }, [navigate, goal.id]);

  const handleDragMove = useCallback(() => {
    wasDragged.current = true;
  }, []);

  const { handleMouseDown } = useDragHandlers({
    goal,
    onDragEnd: (...args) => {
      onUpdateDates(...args);
      // Reset wasDragged after drag ends
      setTimeout(() => {
        wasDragged.current = false;
      }, 0);
    },
    dayWidth,
    itemContainerRef,
    onPreviewDatesChange: (start, end) => {
      setPreviewStartDate(start);
      setPreviewEndDate(end);
    },
    onMouseMove: handleDragMove,
  });
  const [hovered, setHovered] = useState(false);
  const [previewStartDate, setPreviewStartDate] = useState<Date | undefined>();
  const [previewEndDate, setPreviewEndDate] = useState<Date | undefined>();

  const goalColor = useMemo(
    () => getLinkedItemColor(goal.id, { lifeAreas, goals }),
    [goal.id, goals, lifeAreas],
  );

  const completionPercentage = useMemo(
    () =>
      goal
        ? getCompletionPercentage(
            getGoalCompletion(goal, {
              goals,
            }),
          )
        : undefined,
    [goal, goals],
  );

  if (!goal.startDate || !goal.deadline) {
    return null;
  }

  const startDay = differenceInDays(goal.startDate, dateRange.startDate);
  const endDay = differenceInDays(goal.deadline, dateRange.startDate);

  const duration = endDay - startDay;
  if (duration < 0) {
    return null;
  }

  const left = startDay * dayWidth;
  const width = (duration + 1) * dayWidth;

  const onHover = () => setHovered(true);
  const onUnHover = () => setHovered(false);
  const onMove = (e: React.MouseEvent) =>
    handleMouseDown(e, MovementModes.Move);
  const onResizeStart = (e: React.MouseEvent) =>
    handleMouseDown(e, MovementModes.ResizeStart);
  const onResizeEnd = (e: React.MouseEvent) =>
    handleMouseDown(e, MovementModes.ResizeEnd);

  return (
    <>
      <Styled.Bar
        ref={itemContainerRef}
        $left={left}
        $width={width}
        $color={goalColor}
        $completed={!!goal.completedAt}
        $archived={!!goal.archivedAt}
        onContextMenu={openMenu}
        onMouseDown={onMove}
        onMouseEnter={onHover}
        onMouseLeave={onUnHover}
        onClick={onNavigate}
      >
        <Styled.Content>
          <Styled.IconContainer $color={goalColor}>
            <CircleProgress
              percentage={completionPercentage}
              hidePercentage
              isSmall
            />
          </Styled.IconContainer>
          <Styled.NameContainer>{goal.name}</Styled.NameContainer>
        </Styled.Content>

        <Styled.ResizeHandle
          ref={resizeStartHandle}
          $position="start"
          onMouseDown={onResizeStart}
          onClick={stopPropagation}
        >
          <Styled.ResizeIndicator $color={goalColor} />
        </Styled.ResizeHandle>

        <Styled.ResizeHandle
          ref={resizeEndHandle}
          $position="end"
          onMouseDown={onResizeEnd}
          onClick={stopPropagation}
        >
          <Styled.ResizeIndicator $color={goalColor} />
        </Styled.ResizeHandle>

        {hovered && (
          <>
            <Tooltip
              label={format(previewStartDate ?? goal.startDate, 'MMM d')}
              referenceElement={resizeStartHandle}
            />
            <Tooltip
              label={format(previewEndDate ?? goal.deadline, 'MMM d')}
              referenceElement={resizeEndHandle}
            />
          </>
        )}
      </Styled.Bar>

      {!!menuPosition && (
        <GoalContextMenu
          id={goal.id}
          onClose={closeMenu}
          location={menuPosition}
        />
      )}
    </>
  );
};
